abstract class BaseAIError extends Error {
  abstract readonly type: AIErrorType;
}

export enum AIErrorType {
  GeneralNetworkError = 'GeneralNetworkError',
  PaymentRequired = 'PaymentRequired',
  Unauthorized = 'Unauthorized',
  RequestTimeout = 'RequestTimeout',
  QuotaExhausted = 'QuotaExhausted',
  InsufficientCredits = 'InsufficientCredits',
  BudgetExceeded = 'BudgetExceeded',
}

export class UnauthorizedError extends BaseAIError {
  readonly type = AIErrorType.Unauthorized;

  constructor() {
    super('Unauthorized');
  }
}

// user has used up the quota
export class PaymentRequiredError extends BaseAIError {
  readonly type = AIErrorType.PaymentRequired;

  constructor() {
    super('Payment required');
  }
}

// general 500x error
export class GeneralNetworkError extends BaseAIError {
  readonly type = AIErrorType.GeneralNetworkError;

  constructor(message: string = 'Network error') {
    super(message);
  }
}

// request timeout
export class RequestTimeoutError extends BaseAIError {
  readonly type = AIErrorType.RequestTimeout;

  constructor(message: string = 'Request timeout') {
    super(message);
  }
}

// API key quota exhausted (insufficient_quota, billing_hard_limit, etc.)
export class QuotaExhaustedError extends BaseAIError {
  readonly type = AIErrorType.QuotaExhausted;
  readonly keySource: 'user' | 'workspace' | 'server';

  constructor(
    keySource: 'user' | 'workspace' | 'server',
    message: string = 'API quota exhausted'
  ) {
    super(message);
    this.keySource = keySource;
  }
}

// sl-8m4o.1: StreamLain managed credits exhausted (not an error — expected behavior)
export class InsufficientCreditsError extends BaseAIError {
  readonly type = AIErrorType.InsufficientCredits;

  constructor(message: string = 'Keine Credits verfügbar.') {
    super(message);
  }
}

// sl-ufq6.4.1: Budget limit exceeded (user, group, or automation)
export type BudgetExceededCode =
  | 'MANAGED_AI_ACCESS_DENIED'
  | 'USER_BUDGET_EXCEEDED'
  | 'GROUP_BUDGET_EXCEEDED'
  | 'AUTOMATION_BUDGET_EXCEEDED';

export class BudgetExceededError extends BaseAIError {
  readonly type = AIErrorType.BudgetExceeded;
  readonly code: BudgetExceededCode;
  readonly resetAt: string | null;

  constructor(code: BudgetExceededCode, message: string, resetAt?: string) {
    super(message);
    this.code = code;
    this.resetAt = resetAt ?? null;
  }
}

export type AIError =
  | UnauthorizedError
  | PaymentRequiredError
  | GeneralNetworkError
  | RequestTimeoutError
  | QuotaExhaustedError
  | InsufficientCreditsError
  | BudgetExceededError;
